import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { sumBy } from 'lodash'
import { queryClient as qc, querySetting } from 'keys/queryClient'
import { getAssessmentExecutionFetch } from 'store/middlewares/assessment.crud'
import { secretStorage } from 'config'
import { affiliationGroups } from 'keys'
import { gradeNameToLetter, sortGradeLetters, formatGradesLabel, ELEMENTARY_GRADE_LETTERS, MIDDLE_GRADE_LETTERS } from '../constants'

// שקף 2 — מי נמדד: סכימת ההקצאות (המכנה "מתוך Y") מכל בתי הספר הנגישים למשתמש.
// מסונן לקבוצת "בתי ספר" בלבד — אותה ברירת מחדל כמו עמוד נתוני ביצוע (SystemReviewContext),
// אחרת מרכזי למידה נספרים בנוסף ותלמידים המשויכים לשניהם נספרים פעמיים
const useSlide2Data = (assessmentFilters) => {
    const semesterAndYear = assessmentFilters?.semester_and_year
    const { roleIndex } = useSelector(state => state.user)

    const schoolsGroup = useMemo(() => {
        const { user_data } = secretStorage.getItem("user") || {}
        const { affiliation_groups } = user_data?.[roleIndex]?.user_data || {}
        return affiliation_groups?.find(group => group.name === affiliationGroups.schools)
    }, [roleIndex])

    const filters = useMemo(() =>
        schoolsGroup ? { group: schoolsGroup } : {}
    , [schoolsGroup])

    const { data, isLoading, isError } = useQuery({
        queryKey: [qc.assessmentExecution, filters, semesterAndYear],
        queryFn: async ({ signal }) => {
            const { data } = await getAssessmentExecutionFetch({ ...filters, semester_and_year: semesterAndYear }, signal)
            return data
        },
        ...querySetting
    })

    return useMemo(() => {
        const affiliations = data || []

        // שכבות שנמדדו בפועל (מגיעות מהבקנד פר מוסד) + סיווג יסודי/חטיבה
        const gradeLetters = new Set()
        let elementarySchoolCount = 0
        let middleSchoolCount = 0

        affiliations.forEach(aff => {
            const letters = (aff.grades || []).map(gradeNameToLetter)
            letters.forEach(letter => gradeLetters.add(letter))
            if (letters.some(letter => ELEMENTARY_GRADE_LETTERS.includes(letter)))
                elementarySchoolCount++
            if (letters.some(letter => MIDDLE_GRADE_LETTERS.includes(letter)))
                middleSchoolCount++
        })

        const sortedGrades = sortGradeLetters([...gradeLetters])

        return {
            totalStudents: sumBy(affiliations, 'test_taker_count'),
            executedStudents: sumBy(affiliations, 'test_taker_execution_count'),
            schoolCount: affiliations.length,
            elementarySchoolCount,
            middleSchoolCount,
            gradeCount: sortedGrades.length,
            gradesLabel: formatGradesLabel(sortedGrades),
            affiliations,
            isLoading,
            isError
        }
    }, [data, isLoading, isError])
}

export default useSlide2Data
